import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

import { releaseMetadata } from './releaseMetadata';

const CONSOLE_ERROR_LIMIT = 5;
const IGNORED_CONSOLE_PATTERNS = [
  /ResizeObserver loop limit exceeded/i,
  /Non-Error promise rejection captured/i,
];

let appInsightsInstance = null;
let browserTelemetryInitialized = false;
let globalHandlersInstalled = false;
let consolePatched = false;
let originalConsoleError = null;
let lastTrackedPageViewKey = '';

const consoleErrorCounts = new Map();

function getEnvironment() {
  return releaseMetadata.environment || 'development';
}

function getReleaseSha() {
  return releaseMetadata.releaseSha || 'unknown';
}

function getReleaseRef() {
  return releaseMetadata.releaseRef || 'unknown';
}

function getBrowserFamily() {
  const userAgent = navigator.userAgent;
  if (/Edg\//i.test(userAgent)) return 'Edge';
  if (/Chrome\//i.test(userAgent)) return 'Chrome';
  if (/Firefox\//i.test(userAgent)) return 'Firefox';
  if (/Safari\//i.test(userAgent)) return 'Safari';
  return 'Other';
}

function getDeviceType() {
  const userAgent = navigator.userAgent;
  if (/Tablet|iPad/i.test(userAgent)) return 'tablet';
  if (/Mobile|Android|iPhone/i.test(userAgent)) return 'mobile';
  return 'desktop';
}

function sanitizeString(value, maxLength = 500) {
  let sanitized = value;

  sanitized = sanitized.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[redacted-email]');
  sanitized = sanitized.replace(
    /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9._-]+\.[A-Za-z0-9._-]+\b/g,
    '[redacted-jwt]',
  );
  sanitized = sanitized.replace(/\b(Bearer\s+)[A-Za-z0-9\-._~+/]+=*/gi, '$1[redacted-token]');
  sanitized = sanitized.replace(
    /\b(token|sig|signature|code|key|password|authorization|jwt)=([^&\s]+)/gi,
    '$1=[redacted]',
  );

  if (sanitized.length > maxLength) {
    sanitized = `${sanitized.slice(0, maxLength)}…`;
  }

  return sanitized;
}

export function sanitizeUrlForTelemetry(url) {
  if (!url) {
    return '';
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return sanitizeString(url.split('?')[0].split('#')[0], 300);
  }
}

function sanitizeProperties(properties = {}) {
  return Object.entries(properties).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) {
      return acc;
    }

    acc[key] = typeof value === 'string' ? sanitizeString(value) : String(value);
    return acc;
  }, {});
}

function buildCommonProperties(options) {
  return {
    app_name: options.appName,
    environment: options.environment || getEnvironment(),
    release_sha: options.releaseSha || getReleaseSha(),
    release_ref: options.releaseRef || getReleaseRef(),
    browser_family: getBrowserFamily(),
    device_type: getDeviceType(),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
}

function sanitizeTelemetryItem(item, options) {
  const baseData = item.baseData || {};
  const currentProperties = baseData.properties || {};

  baseData.properties = {
    ...sanitizeProperties(currentProperties),
    ...buildCommonProperties(options),
  };

  if (typeof baseData.message === 'string') {
    baseData.message = sanitizeString(baseData.message);
  }

  if (typeof baseData.uri === 'string') {
    baseData.uri = sanitizeUrlForTelemetry(baseData.uri);
  }

  if (typeof baseData.url === 'string') {
    baseData.url = sanitizeUrlForTelemetry(baseData.url);
  }

  if (typeof baseData.data === 'string') {
    baseData.data = sanitizeUrlForTelemetry(baseData.data);
  }

  if (typeof baseData.target === 'string') {
    baseData.target = sanitizeString(baseData.target, 200);
  }

  if (typeof baseData.name === 'string') {
    baseData.name = sanitizeString(baseData.name, 200);
  }

  const exceptions = Array.isArray(baseData.exceptions) ? baseData.exceptions : [];
  exceptions.forEach((exception) => {
    if (typeof exception.message === 'string') {
      exception.message = sanitizeString(exception.message);
    }

    if (typeof exception.typeName === 'string') {
      exception.typeName = sanitizeString(exception.typeName, 120);
    }
  });
}

function normalizeError(error) {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(sanitizeString(error));
  }

  return new Error('Unknown browser error');
}

function installGlobalHandlers(options) {
  if (!appInsightsInstance || globalHandlersInstalled) {
    return;
  }

  window.addEventListener('error', (event) => {
    trackSanitizedException(event.error || event.message, {
      source: 'window.error',
      file: event.filename ? sanitizeUrlForTelemetry(event.filename) : undefined,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    trackSanitizedException(event.reason, {
      source: 'window.unhandledrejection',
    });
  });

  globalHandlersInstalled = true;
  patchConsoleError(options);
}

function patchConsoleError(options) {
  if (!appInsightsInstance || consolePatched) {
    return;
  }

  originalConsoleError = console.error.bind(console);

  console.error = (...args) => {
    originalConsoleError?.(...args);

    const message = sanitizeString(
      args
        .map((arg) => {
          if (arg instanceof Error) {
            return arg.message || arg.name;
          }

          if (typeof arg === 'string') {
            return arg;
          }

          return '';
        })
        .filter(Boolean)
        .join(' | '),
      600,
    );

    if (!message || IGNORED_CONSOLE_PATTERNS.some((pattern) => pattern.test(message))) {
      return;
    }

    const count = consoleErrorCounts.get(message) || 0;
    if (count >= CONSOLE_ERROR_LIMIT) {
      return;
    }

    consoleErrorCounts.set(message, count + 1);

    appInsightsInstance?.trackTrace({
      message,
      severityLevel: SeverityLevel.Error,
      properties: {
        ...buildCommonProperties(options),
        source: 'console.error',
        occurrence: count + 1,
      },
    });
  };

  consolePatched = true;
}

export function initializeBrowserTelemetry(options) {
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
  if (!connectionString) {
    return null;
  }

  if (browserTelemetryInitialized && appInsightsInstance) {
    return appInsightsInstance;
  }

  const samplingPercentage = Number(import.meta.env.VITE_APPINSIGHTS_SAMPLING_PERCENTAGE || '100');

  const instance = new ApplicationInsights({
    config: {
      connectionString,
      samplingPercentage: Number.isFinite(samplingPercentage) ? samplingPercentage : 100,
      autoTrackPageVisitTime: true,
      disableAjaxTracking: false,
      disableFetchTracking: false,
      disableExceptionTracking: false,
      enableCorsCorrelation: false,
      enableRequestHeaderTracking: false,
      enableResponseHeaderTracking: false,
    },
  });

  instance.addTelemetryInitializer((item) => sanitizeTelemetryItem(item, options));
  instance.loadAppInsights();

  appInsightsInstance = instance;
  browserTelemetryInitialized = true;
  installGlobalHandlers(options);

  trackOperationalEvent('browser_telemetry_initialized', {
    app_name: options.appName,
    environment: options.environment || getEnvironment(),
  });

  return appInsightsInstance;
}

export function trackPageView(routeName) {
  if (!appInsightsInstance) {
    return;
  }

  const currentUri = sanitizeUrlForTelemetry(window.location.href);
  const pageViewKey = `${routeName}|${currentUri}`;
  if (pageViewKey === lastTrackedPageViewKey) {
    return;
  }

  lastTrackedPageViewKey = pageViewKey;

  appInsightsInstance.trackPageView({
    name: sanitizeString(routeName, 200),
    uri: currentUri,
  });
}

export function trackOperationalEvent(eventName, properties = {}) {
  if (!appInsightsInstance) {
    return;
  }

  appInsightsInstance.trackEvent({
    name: sanitizeString(eventName, 120),
    properties: sanitizeProperties(properties),
  });
}

export function trackSanitizedException(error, properties = {}) {
  if (!appInsightsInstance) {
    return;
  }

  appInsightsInstance.trackException({
    exception: normalizeError(error),
    properties: sanitizeProperties(properties),
    severityLevel: SeverityLevel.Error,
  });
}

export function trackBackendRequestFailure(source, url, method, status) {
  trackOperationalEvent('browser_api_request_failed', {
    source,
    method: method.toUpperCase(),
    status: status ?? 'network_error',
    url: sanitizeUrlForTelemetry(url),
  });
}
